import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../auth/api';
import { useAuth } from '../../auth/AuthContext';
import CategoryManager from '../../components/CategoryManager';
import ExerciseForm from '../../components/ExerciseForm';
import ExerciseList from '../../components/ExerciseList';
import ChildProfileModal from '../../components/ChildProfileModal';
import CaseHistoryTab from './CaseHistory/CaseHistoryTab';
import '../../components/styles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// A recognized_ipa value that is entirely generic "Sound N" placeholders (legacy bad data).
const PLACEHOLDER_IPA_RE = /^(Sound \d+)(\s+Sound \d+)*$/;
const sanitizeIpa = (value) => (value && PLACEHOLDER_IPA_RE.test(value.trim()) ? '' : (value || ''));

// Two-level dashboard navigation. Children is flat; the rest carry sub-tabs.
const SUB_TABS = {
  articulation: ['categories', 'exercises', 'assignment', 'manage-assignment', 'progress', 'review'],
  auditory: ['exercises', 'assignment', 'manage-assignment', 'progress'],
  'oral-motor': ['assignment', 'sessions'],
  'case-history': ['history', 'clinical', 'measures', 'sign-off', 'withdrawals'],
};

const MAIN_TAB_LABELS = {
  children: 'Children',
  articulation: 'Articulation Exercises',
  auditory: 'Auditory Exercises',
  'oral-motor': 'Mouth Gym',
  'case-history': 'Case History',
};

const SUB_TAB_LABELS = {
  categories: 'Categories',
  exercises: 'Exercises',
  assignment: 'Assignment',
  'manage-assignment': 'Manage Assignment',
  progress: 'Progress',
  review: 'Review',
  sessions: 'Sessions',
  history: 'Interview',
  clinical: 'Clinical',
  measures: 'Measures',
  'sign-off': 'Sign-off',
  withdrawals: 'Withdrawals',
};

// Accent color per main tab, preserved from the original flat-tab styling.
const MAIN_TAB_ACCENT = {
  auditory: '#AB47BC',
  'oral-motor': '#FF9800',
};



export default function TherapistPage() {
  const { user, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [childrenError, setChildrenError] = useState('');
  const [organisations, setOrganisations] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [progress, setProgress] = useState(null);
  const [showChildModal, setShowChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState(null);

  const [filters, setFilters] = useState({
    language: 'all',
    category_id: 'all',
    sub_category_id: 'all',
    search: ''
  });
  const [phonemes, setPhonemes] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [selectedProgressChild, setSelectedProgressChild] = useState('');
  const [assignmentSize, setAssignmentSize] = useState(5);
  const [selectedManageChild, setSelectedManageChild] = useState('');
  const [manageAssignments, setManageAssignments] = useState([]);
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTab = searchParams.get('tab') || 'children';
  const subTab = searchParams.get('sub') || (SUB_TABS[mainTab] ? SUB_TABS[mainTab][0] : null);

  // Switch main tab; default its sub-tab to the first in the group (omit sub for flat tabs).
  const goMain = (tab) => {
    const params = { tab };
    if (SUB_TABS[tab]) params.sub = SUB_TABS[tab][0];
    setSearchParams(params);
  };
  // Switch sub-tab within the current main tab.
  const goSub = (sub) => setSearchParams({ tab: mainTab, sub });

  const [exerciseListRefreshKey, setExerciseListRefreshKey] = useState(0);
  const [notesMap, setNotesMap] = useState({}); // To store notes for each progress record

  // Therapist Review Workflow State
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewStatusFilter, setReviewStatusFilter] = useState('pending');
  const [reviewChildFilter, setReviewChildFilter] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [expandedReview, setExpandedReview] = useState(null);
  const [reviewEdits, setReviewEdits] = useState({}); // { [progressId]: { field: value } }
  const [reviewAudioUrls, setReviewAudioUrls] = useState({}); // { [progressId]: objectURL | 'unavailable' | 'loading' }
  const ipaAutoFillTimers = useRef({}); // { [progressId]: debounce timer id } for transcript -> IPA
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [selectedExercisesForAssignment, setSelectedExercisesForAssignment] = useState([]);

  // Auditory Discrimination State
  const [auditoryExercises, setAuditoryExercises] = useState([]);
  const [showAuditoryForm, setShowAuditoryForm] = useState(false);
  const [editingAuditoryExercise, setEditingAuditoryExercise] = useState(null);
  const defaultAuditoryForm = {
    exercise_type: 'image_discrimination',
    target_word: '',
    target_image_url: '',
    language: 'hi-IN',
    difficulty_level: 1,
    instruction_text: '',
    options: [
      { option_text: '', image_url: '', is_correct: true, display_order: 1 },
      { option_text: '', image_url: '', is_correct: false, display_order: 2 },
      { option_text: '', image_url: '', is_correct: false, display_order: 3 },
      { option_text: '', image_url: '', is_correct: false, display_order: 4 }
    ]
  };
  const [auditoryForm, setAuditoryForm] = useState(defaultAuditoryForm);
  const [auditoryAssignChild, setAuditoryAssignChild] = useState('');
  const [selectedAuditoryExercises, setSelectedAuditoryExercises] = useState([]);
  const [auditoryProgress, setAuditoryProgress] = useState(null);
  const [auditoryProgressChild, setAuditoryProgressChild] = useState('');
  // Manage auditory assignments state
  const [manageAuditoryChild, setManageAuditoryChild] = useState('');
  const [manageAuditoryAssignments, setManageAuditoryAssignments] = useState([]);

  // Auditory filter state
  const [auditoryFilters, setAuditoryFilters] = useState({
    exercise_type: 'all',
    language: 'all',
    difficulty_level: 'all',
    search: ''
  });
  const [showAuditoryFilters, setShowAuditoryFilters] = useState(false);

  // Oral Motor / Mouth Gym state
  const [oralMotorExercises, setOralMotorExercises] = useState([]);
  const [selectedOralExercises, setSelectedOralExercises] = useState([]);
  const [oralAssignChild, setOralAssignChild] = useState('');
  const [oralSessions, setOralSessions] = useState([]);
  const [oralSessionsChild, setOralSessionsChild] = useState('');

  useEffect(() => {
    fetchChildren();
    fetchOrganisations();
    fetchPhonemes();
    fetchCategories();
    fetchExercises();
    fetchAuditoryExercises();
    fetchOralMotorExercises();
  }, []);

  const fetchOrganisations = async () => {
    try {
      const response = await api.get('/organisations/');
      setOrganisations(response.data);
    } catch (error) {
      console.error('Error fetching organisations:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get(`/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPhonemes = async () => {
    try {
      const response = await api.get(`/phonemes/`);
      setPhonemes(response.data);
    } catch (error) {
      console.error('Error fetching phonemes:', error);
    }
  };

  const fetchChildren = async () => {
    try {
      const response = await api.get(`/children/`);
      setChildren(response.data);
      setChildrenError('');
    } catch (error) {
      console.error('Error fetching children:', error);
      // Without this the list just looks empty, and a failed request is
      // indistinguishable from an organisation that has no children yet.
      setChildrenError(
        error.response
          ? `Could not load children (server said ${error.response.status}). If the backend was just updated, its database migrations may not have been run yet.`
          : 'Could not load children. Check that the backend is running.'
      );
    }
  };

  const fetchExercises = async (currentFilters = filters) => {
    try {
      const params = {};
      if (currentFilters.language !== 'all') params.language = currentFilters.language;
      if (currentFilters.category_id !== 'all') params.category_id = currentFilters.category_id;
      if (currentFilters.sub_category_id !== 'all') params.sub_category_id = currentFilters.sub_category_id;
      if (currentFilters.search) params.search = currentFilters.search;

      const response = await api.get(`/exercises`, { params });
      setExercises(response.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const handleSaveChild = async (childData) => {
    try {
      if (editingChild) {
        await api.put(`/children/${editingChild.id}`, childData);
        alert('Child updated successfully!');
      } else {
        await api.post(`/children/`, childData);
        alert('Child created successfully!');
      }
      setShowChildModal(false);
      setEditingChild(null);
      fetchChildren();
    } catch (error) {
      console.error('Error saving child:', error);
      let errorMsg = 'Error saving child';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg = detail.map(err => `${err.loc[err.loc.length - 1]}: ${err.msg}`).join('\n');
        } else {
          errorMsg = detail;
        }
      }
      alert(errorMsg);
    }
  };

  const handleDeleteChild = async (childId) => {
    if (!window.confirm('WARNING: Are you sure you want to permanently delete this child? All progress, assignments, and assessments will be deleted.')) {
      return;
    }
    try {
      await api.delete(`/children/${childId}`);
      alert('Child deleted successfully.');
      fetchChildren();
    } catch (error) {
      console.error('Error deleting child:', error);
      alert('Failed to delete child.');
    }
  };

  const assignExercises = async (childId, exerciseIds) => {
    try {
      await api.post(`/assignments/`, {
        child_id: childId,
        exercise_ids: exerciseIds
      });
      alert(`Assigned ${exerciseIds.length} exercises successfully!`);
    } catch (error) {
      console.error('Error assigning exercises:', error);
      alert('Error assigning exercises');
    }
  };



  const handleFilterChange = (filter, value) => {
    const newFilters = { ...filters, [filter]: value };

    if (filter === 'category_id') {
      newFilters.sub_category_id = 'all';
      if (value === 'all') {
        setSubCategories([]);
      } else {
        const cat = categories.find(c => c.id === parseInt(value));
        setSubCategories(cat?.sub_categories || []);
      }
    }

    setFilters(newFilters);
    fetchExercises(newFilters);
  };

  const toggleExerciseSelection = (exerciseId) => {
    setSelectedExercises(prev =>
      prev.includes(exerciseId)
        ? prev.filter(id => id !== exerciseId)
        : [...prev, exerciseId]
    );
  };

  const selectRandomExercises = () => {
    if (exercises.length === 0) {
      alert('No exercises available. Please apply filters first.');
      return;
    }

    const shuffled = [...exercises].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, assignmentSize).map(ex => ex.id);
    setSelectedExercises(selected);
  };

  const handleAssign = () => {
    if (!selectedChild) {
      alert('Please select a child');
      return;
    }
    if (selectedExercises.length === 0) {
      alert('Please select at least one exercise');
      return;
    }
    if (selectedExercises.length < 5) {
      alert(`Please select at least 5 exercises. Currently selected: ${selectedExercises.length}`);
      return;
    }
    assignExercises(parseInt(selectedChild), selectedExercises);
    setSelectedExercises([]);
  };

  const handleProgressSelect = async (childId) => {
    setSelectedProgressChild(childId);
    try {
      const response = await api.get(`/progress/${childId}`);
      setProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const handleClearReport = () => {
    setProgress(null);
    setSelectedProgressChild('');
  };

  const handleManageChildSelect = async (childId) => {
    setSelectedManageChild(childId);
    if (childId) {
      try {
        const response = await api.get(`/assignments/${childId}`);
        setManageAssignments(response.data);
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setManageAssignments([]);
      }
    } else {
      setManageAssignments([]);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await api.delete(`/assignments/delete/${assignmentId}`);
      alert('Assignment deleted successfully!');
      // Refresh the assignments list
      handleManageChildSelect(selectedManageChild);
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Error deleting assignment');
    }
  };

  const updateProgressNotes = async (progressId, notes) => {
    try {
      await api.put(`/progress/${progressId}/notes`, { notes });

      // Update the local state
      setNotesMap(prev => ({
        ...prev,
        [progressId]: notes
      }));

      // Update the progress record in the state
      if (progress) {
        setProgress(prev => ({
          ...prev,
          progress_records: prev.progress_records.map(record =>
            record.id === progressId ? { ...record, notes } : record
          )
        }));
      }

      alert('Notes saved successfully!');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Error saving notes');
    }
  };

  // ===== Therapist Review Workflow Handlers =====

  const loadReviewQueue = async (status = reviewStatusFilter, childId = reviewChildFilter) => {
    setReviewLoading(true);
    try {
      const params = { status };
      if (childId) params.child_id = childId;
      const response = await api.get('/review/pending', { params });
      setReviewQueue(response.data.records || []);
    } catch (error) {
      console.error('Error fetching review queue:', error);
      setReviewQueue([]);
    } finally {
      setReviewLoading(false);
    }
  };

  // Reload the queue whenever the Review sub-tab is opened or its filters change
  useEffect(() => {
    if (mainTab === 'articulation' && subTab === 'review') {
      loadReviewQueue(reviewStatusFilter, reviewChildFilter);
    }
  }, [mainTab, subTab, reviewStatusFilter, reviewChildFilter]);

  // Seed the edit buffer from the record's current values when a row is expanded
  const toggleReviewRow = (record) => {
    if (expandedReview === record.id) {
      // Collapse: free the audio object URL
      setReviewAudioUrls(prev => {
        const url = prev[record.id];
        if (url && url !== 'unavailable' && url !== 'loading') URL.revokeObjectURL(url);
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
      setExpandedReview(null);
      return;
    }
    setReviewEdits(prev => ({
      ...prev,
      [record.id]: {
        therapist_label: record.therapist_label || '',
        error_type: record.error_type || '',
        severity_level: record.severity_level || '',
        notes: record.notes || '',
        audio_quality: record.audio_quality || '',
        has_background_noise: !!record.has_background_noise,
        recognized_text: record.recognized_text || '',
        expected_ipa: record.expected_ipa || '',
        recognized_ipa: sanitizeIpa(record.recognized_ipa),
      }
    }));
    setExpandedReview(record.id);
    loadReviewAudio(record);
  };

  const loadReviewAudio = async (record) => {
    if (!record.has_audio) {
      setReviewAudioUrls(prev => ({ ...prev, [record.id]: 'unavailable' }));
      return;
    }
    setReviewAudioUrls(prev => ({ ...prev, [record.id]: 'loading' }));
    try {
      const response = await api.get(`/progress/${record.id}/audio`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      setReviewAudioUrls(prev => ({ ...prev, [record.id]: url }));
    } catch (error) {
      console.error('Error loading audio:', error);
      setReviewAudioUrls(prev => ({ ...prev, [record.id]: 'unavailable' }));
    }
  };

  const handleReviewEditChange = (progressId, field, value) => {
    setReviewEdits(prev => {
      const row = { ...prev[progressId], [field]: value };
      // Once the therapist edits the IPA by hand, stop auto-overwriting it.
      if (field === 'recognized_ipa') row.__ipaManual = true;
      return { ...prev, [progressId]: row };
    });

    // When the transcript changes, auto-fill Recognized IPA from it (debounced),
    // unless the therapist has manually overridden the IPA field.
    if (field === 'recognized_text') {
      if (ipaAutoFillTimers.current[progressId]) {
        clearTimeout(ipaAutoFillTimers.current[progressId]);
      }
      ipaAutoFillTimers.current[progressId] = setTimeout(() => {
        autoFillRecognizedIpa(progressId, value);
      }, 400);
    }
  };

  const autoFillRecognizedIpa = async (progressId, text) => {
    try {
      const res = await api.post(`/progress/${progressId}/recognized-ipa-preview`, { text });
      const ipa = res.data?.ipa || '';
      setReviewEdits(prev => {
        const row = prev[progressId] || {};
        // Respect a manual override; also skip if the backend couldn't derive anything.
        if (row.__ipaManual || !ipa) return prev;
        return { ...prev, [progressId]: { ...row, recognized_ipa: ipa } };
      });
    } catch (error) {
      console.error('Error auto-filling recognized IPA:', error);
    }
  };

  const saveReviewEdits = async (progressId) => {
    const { __ipaManual, ...edits } = reviewEdits[progressId] || {};
    const updated = await api.put(`/progress/${progressId}/review`, edits);
    // Reflect saved values back into the queue
    setReviewQueue(prev => prev.map(r => (r.id === progressId ? { ...r, ...updated.data.record } : r)));
    return updated.data.record;
  };

  const handleSaveReview = async (progressId) => {
    try {
      await saveReviewEdits(progressId);
      alert('Edits saved.');
    } catch (error) {
      console.error('Error saving review edits:', error);
      alert('Error saving edits');
    }
  };

  const handleReviewDecision = async (progressId, status) => {
    try {
      // Persist any pending edits first, then record the decision
      await saveReviewEdits(progressId);
      await api.post(`/progress/${progressId}/decision`, { status });
      setExpandedReview(null);
      // Drop it from the current view if it no longer matches the active filter
      if (reviewStatusFilter !== status) {
        setReviewQueue(prev => prev.filter(r => r.id !== progressId));
      } else {
        setReviewQueue(prev => prev.map(r => (r.id === progressId ? { ...r, review_status: status } : r)));
      }
    } catch (error) {
      console.error('Error submitting decision:', error);
      alert('Error submitting decision');
    }
  };

  // ===== Auditory Discrimination Handlers =====

  const fetchAuditoryExercises = async (currentFilters = auditoryFilters) => {
    try {
      const params = {};
      if (currentFilters.exercise_type !== 'all') params.exercise_type = currentFilters.exercise_type;
      if (currentFilters.language !== 'all') params.language = currentFilters.language;
      if (currentFilters.difficulty_level !== 'all') params.difficulty_level = parseInt(currentFilters.difficulty_level);
      // Note: search is handled client-side since the backend doesn't support it
      const resp = await api.get('/auditory/exercises', { params });
      setAuditoryExercises(resp.data);
    } catch (error) {
      console.error('Error fetching auditory exercises:', error);
    }
  };

  const handleAuditoryFilterChange = (filter, value) => {
    const newFilters = { ...auditoryFilters, [filter]: value };
    setAuditoryFilters(newFilters);
    if (filter !== 'search') {
      fetchAuditoryExercises(newFilters);
    }
  };

  const clearAuditoryFilters = () => {
    const defaultFilters = { exercise_type: 'all', language: 'all', difficulty_level: 'all', search: '' };
    setAuditoryFilters(defaultFilters);
    fetchAuditoryExercises(defaultFilters);
  };

  // client-side search filter
  const filteredAuditoryExercises = auditoryFilters.search
    ? auditoryExercises.filter(ex =>
      ex.target_word.toLowerCase().includes(auditoryFilters.search.toLowerCase()) ||
      (ex.instruction_text && ex.instruction_text.toLowerCase().includes(auditoryFilters.search.toLowerCase()))
    )
    : auditoryExercises;

  const activeAuditoryFilterCount = [auditoryFilters.exercise_type, auditoryFilters.language, auditoryFilters.difficulty_level].filter(v => v !== 'all').length + (auditoryFilters.search ? 1 : 0);

  const handleAuditoryImageUpload = async (file, callback) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post('/exercises/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      callback(response.data.image_url);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  const resetAuditoryForm = () => {
    setAuditoryForm({ ...defaultAuditoryForm, options: defaultAuditoryForm.options.map(o => ({ ...o })) });
    setEditingAuditoryExercise(null);
    setShowAuditoryForm(false);
  };

  const handleEditAuditoryExercise = (exercise) => {
    setEditingAuditoryExercise(exercise);
    const opts = exercise.options && exercise.options.length > 0
      ? exercise.options.map((o, i) => ({
        option_text: o.option_text || '',
        image_url: o.image_url || '',
        is_correct: o.is_correct,
        display_order: o.display_order || i + 1
      }))
      : defaultAuditoryForm.options.map(o => ({ ...o }));
    setAuditoryForm({
      exercise_type: exercise.exercise_type,
      target_word: exercise.target_word,
      target_image_url: exercise.target_image_url || '',
      language: exercise.language,
      difficulty_level: exercise.difficulty_level,
      instruction_text: exercise.instruction_text || '',
      options: opts
    });
    setShowAuditoryForm(true);
  };

  const handleCreateOrUpdateAuditoryExercise = async () => {
    if (!auditoryForm.target_word.trim()) {
      alert('Target word is required');
      return;
    }
    const filledOptions = auditoryForm.options.filter(o => o.option_text.trim());
    if (filledOptions.length < 2) {
      alert('At least 2 options are required');
      return;
    }

    try {
      if (editingAuditoryExercise) {
        await api.put(`/auditory/exercises/${editingAuditoryExercise.id}`, {
          ...auditoryForm,
          options: filledOptions
        });
        alert('Auditory exercise updated!');
      } else {
        await api.post('/auditory/exercises', {
          ...auditoryForm,
          options: filledOptions
        });
        alert('Auditory exercise created!');
      }
      resetAuditoryForm();
      fetchAuditoryExercises();
    } catch (error) {
      console.error('Error saving auditory exercise:', error);
      alert('Error saving exercise: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAuditoryExercise = async (id) => {
    if (!window.confirm('Delete this auditory exercise?')) return;
    try {
      await api.delete(`/auditory/exercises/${id}`);
      fetchAuditoryExercises();
      setSelectedAuditoryExercises(prev => prev.filter(eid => eid !== id));
    } catch (error) {
      console.error('Error deleting auditory exercise:', error);
      alert('Error deleting exercise');
    }
  };

  const toggleAuditorySelection = (id) => {
    setSelectedAuditoryExercises(prev =>
      prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
    );
  };

  const handleAssignAuditoryExercises = async () => {
    if (!auditoryAssignChild || selectedAuditoryExercises.length === 0) return;
    try {
      await api.post('/auditory/assignments', {
        child_id: parseInt(auditoryAssignChild),
        exercise_ids: selectedAuditoryExercises
      });
      alert(`Assigned ${selectedAuditoryExercises.length} auditory exercises!`);
      setSelectedAuditoryExercises([]);
      setAuditoryAssignChild('');
    } catch (error) {
      console.error('Error assigning auditory exercises:', error);
      alert('Error assigning exercises: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAuditoryProgressSelect = async (childId) => {
    setAuditoryProgressChild(childId);
    if (childId) {
      try {
        const resp = await api.get(`/auditory/progress/${childId}`);
        setAuditoryProgress(resp.data);
      } catch (error) {
        console.error('Error fetching auditory progress:', error);
        setAuditoryProgress(null);
      }
    } else {
      setAuditoryProgress(null);
    }
  };

  // Manage Auditory Assignments
  const handleManageAuditoryChildSelect = async (childId) => {
    setManageAuditoryChild(childId);
    if (childId) {
      try {
        const resp = await api.get(`/auditory/assignments/${childId}`);
        setManageAuditoryAssignments(resp.data);
      } catch (error) {
        console.error('Error fetching auditory assignments:', error);
        setManageAuditoryAssignments([]);
      }
    } else {
      setManageAuditoryAssignments([]);
    }
  };

  const handleDeleteAuditoryAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this auditory assignment?')) return;
    try {
      await api.delete(`/auditory/assignments/${assignmentId}`);
      alert('Auditory assignment deleted!');
      handleManageAuditoryChildSelect(manageAuditoryChild);
    } catch (error) {
      console.error('Error deleting auditory assignment:', error);
      alert('Error deleting assignment');
    }
  };

  // ===== Oral Motor / Mouth Gym Handlers =====

  const fetchOralMotorExercises = async () => {
    try {
      const res = await api.get('/oral-motor/exercises');
      if (res.data.length === 0) {
        await api.post('/oral-motor/seed');
        const res2 = await api.get('/oral-motor/exercises');
        setOralMotorExercises(res2.data);
      } else {
        setOralMotorExercises(res.data);
      }
    } catch (err) {
      console.error('Error fetching oral motor exercises:', err);
    }
  };

  const toggleOralExerciseSelection = (id) => {
    setSelectedOralExercises(prev =>
      prev.includes(id) ? prev.filter(eid => eid !== id) : [...prev, id]
    );
  };

  const handleAssignOralExercises = async () => {
    if (!oralAssignChild || selectedOralExercises.length === 0) {
      alert('Please select a child and at least one exercise');
      return;
    }
    try {
      await api.post('/oral-motor/assignments', {
        child_id: parseInt(oralAssignChild),
        exercise_ids: selectedOralExercises
      });
      alert(`Assigned ${selectedOralExercises.length} Mouth Gym exercises!`);
      setSelectedOralExercises([]);
      setOralAssignChild('');
    } catch (err) {
      console.error('Error assigning oral motor exercises:', err);
      alert('Error assigning exercises');
    }
  };

  const handleOralSessionsSelect = async (childId) => {
    setOralSessionsChild(childId);
    if (childId) {
      try {
        const res = await api.get(`/oral-motor/sessions/${childId}`);
        setOralSessions(res.data);
      } catch (err) {
        console.error('Error fetching oral motor sessions:', err);
        setOralSessions([]);
      }
    } else {
      setOralSessions([]);
    }
  };

  return (
    <div className="therapist-page">
      <header className="page-header">
        <h1>Therapist Dashboard</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>Welcome back, {user?.username}</span>
          {/* <a href="/child" className="nav-link">Go to Child Page →</a> */}
          <button onClick={logout} style={{ padding: '6px 14px', background: '#f44336', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Logout</button>
        </div>
      </header>

      {/* Main navigation */}
      <div className="therapist-tabs">
        {['children', 'articulation', 'auditory', 'oral-motor', 'case-history'].map(tab => (
          <button
            key={tab}
            className={`tab-button ${mainTab === tab ? 'active' : ''}`}
            onClick={() => goMain(tab)}
            style={MAIN_TAB_ACCENT[tab] ? { borderBottomColor: mainTab === tab ? MAIN_TAB_ACCENT[tab] : 'transparent' } : undefined}
          >
            {MAIN_TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Secondary navigation (sub-tabs) for the active main tab */}
      {SUB_TABS[mainTab] && (
        <div className="therapist-subtabs">
          {SUB_TABS[mainTab].map(sub => (
            <button
              key={sub}
              className={`subtab-button ${subTab === sub ? 'active' : ''}`}
              onClick={() => goSub(sub)}
            >
              {SUB_TAB_LABELS[sub]}
            </button>
          ))}
        </div>
      )}

      {/* Case History — the intake workflow's therapist side. Everything it needs
          lives in CaseHistory/, so this file only routes to it. */}
      {mainTab === 'case-history' && (
        <div className="section">
          <CaseHistoryTab subTab={subTab} onSubTab={goSub} />
        </div>
      )}

      {/* Categories Tab */}
      {mainTab === 'articulation' && subTab === 'categories' && (
        <CategoryManager />
      )}

      {/* Exercises Tab */}
      {mainTab === 'articulation' && subTab === 'exercises' && (
        <div>
          <div className="section">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Exercise Management</h2>
              <button
                onClick={() => {
                  setEditingExercise(null);
                  setShowExerciseForm(true);
                }}
                className="btn btn-primary"
              >
                + Create Exercise
              </button>
            </div>

            <ExerciseList
              key={exerciseListRefreshKey}
              onEdit={(exercise) => {
                setEditingExercise(exercise);
                setShowExerciseForm(true);
              }}
              onSelect={(ids) => setSelectedExercisesForAssignment(ids)}
              selectable={false}
            />
          </div>

          {/* Exercise Form Modal */}
          {showExerciseForm && (
            <div className="modal-overlay" onClick={() => setShowExerciseForm(false)}>
              <div className="modal-content exercise-form-modal" onClick={(e) => e.stopPropagation()}>
                <ExerciseForm
                  exercise={editingExercise}
                  onSave={() => {
                    setShowExerciseForm(false);
                    setEditingExercise(null);
                    // Force the ExerciseList to remount and refetch
                    setExerciseListRefreshKey(k => k + 1);
                  }}
                  onCancel={() => {
                    setShowExerciseForm(false);
                    setEditingExercise(null);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {mainTab === 'children' && (
        <div>
          <div className="section">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Children & Profiles</h2>
              <button className="btn btn-primary" onClick={() => {
                setEditingChild(null);
                setShowChildModal(true);
              }}>
                + Create New Child
              </button>
            </div>

            {childrenError && (
              <div style={{ background: '#fdf1ef', border: '1px solid #f1b5ab', color: '#7a271a', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: '0.9rem' }}>
                {childrenError}{' '}
                <button className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={fetchChildren}>Retry</button>
              </div>
            )}

            <div className="children-list-grid">
              {children.length === 0 ? (
                <p>{childrenError ? '' : 'No children found.'}</p>
              ) : (
                children.map(child => (
                  <div key={child.id} className="child-card" style={{ border: '1px solid #ddd', padding: '15px', borderRadius: '8px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h3 style={{ margin: '0 0 5px 0' }}>{child.name}</h3>
                        <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
                          {child.date_of_birth && <span>Age: <strong>{new Date().getFullYear() - new Date(child.date_of_birth).getFullYear()} yrs</strong> | </span>}
                          <span>Group: <strong>{child.age_group}</strong></span> |
                          <span>Severity: <strong>{child.severity}</strong></span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-secondary" onClick={() => {
                          setEditingChild(child);
                          setShowChildModal(true);
                        }}>✏️ Edit</button>
                        <button className="btn btn-danger" style={{ background: '#f44336', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }} onClick={() => handleDeleteChild(child.id)}>🗑️ Delete</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {showChildModal && (
              <ChildProfileModal
                child={editingChild}
                organisations={organisations}
                onSave={handleSaveChild}
                onCancel={() => {
                  setShowChildModal(false);
                  setEditingChild(null);
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Articulation › Assignment — filter + pick exercises + assign to a child */}
      {mainTab === 'articulation' && subTab === 'assignment' && (
        <div>
          <div className="section">
            <h2>Filter Exercises</h2>
            <div className="exercise-filters assignment-filters">
              <div className="filter-group">
                <label>Language:</label>
                <select
                  value={filters.language}
                  onChange={(e) => handleFilterChange('language', e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="hi-IN">Hindi</option>
                  <option value="en-US">English</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Category:</label>
                <select
                  value={filters.category_id}
                  onChange={(e) => handleFilterChange('category_id', e.target.value)}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {subCategories.length > 0 && (
                <div className="filter-group">
                  <label>SubCategory:</label>
                  <select
                    value={filters.sub_category_id}
                    onChange={(e) => handleFilterChange('sub_category_id', e.target.value)}
                  >
                    <option value="all">All SubCategories</option>
                    {subCategories.map(subCat => (
                      <option key={subCat.id} value={subCat.id}>
                        {subCat.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="filter-group search-group">
                <label>Search:</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search by word..."
                />
              </div>
            </div>
          </div>

          <div className="section">
            <h2>Assign Exercises</h2>

            <div className="assignment-controls">
              <select
                id="assignChild"
                name="assignChild"
                value={selectedChild}
                onChange={(e) => setSelectedChild(e.target.value)}
              >
                <option value="">Select a Child</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name} (Group {child.age_group}, {child.severity})
                  </option>
                ))}
              </select>

              <div className="assignment-size">
                <label>Words per exercise:</label>
                <select
                  id="assignmentSize"
                  name="assignmentSize"
                  value={assignmentSize}
                  onChange={(e) => setAssignmentSize(parseInt(e.target.value))}
                >
                  <option value={5}>5 words</option>
                  <option value={8}>8 words</option>
                  <option value={10}>10 words</option>
                  <option value={12}>12 words</option>
                </select>
              </div>

              <button onClick={selectRandomExercises} className="random-button">
                🎲 Select {assignmentSize} Random Exercises
              </button>
            </div>

            <div className="selected-count">
              <strong>Selected: {selectedExercises.length} / {assignmentSize} words</strong>
              {selectedExercises.length > 0 && (
                <button
                  onClick={() => setSelectedExercises([])}
                  className="clear-button"
                >
                  Clear All
                </button>
              )}
            </div>

            <div className="exercises-grid">
              {exercises.map(exercise => (
                <div
                  key={exercise.id}
                  className={`exercise-card ${selectedExercises.includes(exercise.id) ? 'selected' : ''}`}
                  onClick={() => toggleExerciseSelection(exercise.id)}
                >
                  <div className="exercise-image">
                    {exercise.image_url ? (
                      <img
                        src={exercise.image_url.startsWith('http')
                          ? exercise.image_url
                          : `${BACKEND_URL}${exercise.image_url}`}
                        alt={exercise.word_text}
                      />
                    ) : (
                      <div className="image-placeholder">
                        {exercise.word_text.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="exercise-info">
                    <h4>{exercise.word_text}</h4>
                    <span className="language-badge">
                      {exercise.language === 'hi-IN' ? '🇮🇳 Hindi' : '🇺🇸 English'}
                    </span>
                    <div className="exercise-meta">
                      <span className="category-tag">{categories.find(c => c.id === exercise.category_id)?.name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleAssign}
              disabled={!selectedChild || selectedExercises.length < 5}
              className="assign-button"
            >
              {selectedExercises.length >= 5
                ? `Assign ${selectedExercises.length} Exercises`
                : `Select at least 5 exercises (${selectedExercises.length}/5)`
              }
            </button>
          </div>
        </div>
      )}

      {mainTab === 'articulation' && subTab === 'manage-assignment' && (
        <div className="section">
          <h2>Manage Assignments</h2>
          <select
            id="manageChild"
            name="manageChild"
            value={selectedManageChild}
            onChange={(e) => handleManageChildSelect(e.target.value)}
          >
            <option value="">Select a Child</option>
            {children.map(child => (
              <option key={child.id} value={child.id}>{child.name}</option>
            ))}
          </select>

          {manageAssignments.length > 0 && (
            <div className="assignments-list">
              <h3>Active Assignments for {children.find(c => c.id == selectedManageChild)?.name}</h3>
              {manageAssignments.map(assignment => {
                const exerciseIds = JSON.parse(assignment.exercise_ids);
                return (
                  <div key={assignment.id} className="assignment-item">
                    <div className="assignment-info">
                      <strong>Assignment #{assignment.id}</strong>
                      <p>{exerciseIds.length} exercises assigned</p>
                      <small>Assigned on: {new Date(assignment.created_at).toLocaleDateString()}</small>
                    </div>
                    <button
                      onClick={() => handleDeleteAssignment(assignment.id)}
                      className="delete-button"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {selectedManageChild && manageAssignments.length === 0 && (
            <p style={{ marginTop: '20px', color: '#666' }}>No active assignments for this child.</p>
          )}
        </div>
      )}

      {mainTab === 'articulation' && subTab === 'progress' && (
        <div className="section">
          <h2>Progress Reports</h2>
          <select
            id="progressChild"
            name="progressChild"
            value={selectedProgressChild}
            onChange={(e) => handleProgressSelect(e.target.value)}
          >
            <option value="">Select a Child</option>
            {children.map(child => (
              <option key={child.id} value={child.id}>{child.name}</option>
            ))}
          </select>

          {progress && (
            <button onClick={handleClearReport} className="clear-button" style={{ marginLeft: '10px' }}>
              Clear Report
            </button>
          )}

          {progress && (
            <div className="progress-report">
              <h3>Progress Report for {children.find(c => c.id == selectedProgressChild)?.name}</h3>

              <div className="progress-summary">
                <div className="stat">
                  <h4>Total Sessions</h4>
                  <p>{progress.summary.total_sessions}</p>
                </div>
                <div className="stat">
                  <h4>Avg. Pronunciation</h4>
                  <p>{progress.summary.average_pronunciation}%</p>
                </div>
                <div className="stat">
                  <h4>Avg. Accuracy</h4>
                  <p>{progress.summary.average_accuracy}%</p>
                </div>
              </div>

              {progress.summary.total_sessions > 0 && (
                <>
                  <div className="error-breakdown">
                    <h4>Error Type Breakdown</h4>
                    {Object.entries(progress.summary.error_breakdown).map(([error, count]) => (
                      <div key={error} className="error-item">
                        <span className="error-type">{error}:</span>
                        <span className="error-count">{count}</span>
                      </div>
                    ))}
                  </div>

                  <div className="progress-details">
                    <h4>Session Details</h4>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Exercise</th>
                            <th>Target Word</th>
                            <th>What Child Said</th>
                            <th>Score</th>
                            <th>Error Type</th>
                            <th>Severity</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {progress.progress_records.map(record => (
                            <React.Fragment key={record.id}>
                              <tr>
                                <td>{new Date(record.session_date).toLocaleDateString()}</td>
                                <td>
                                  Exercise #{record.exercise_id}
                                  {record.target_exercises && (
                                    <span style={{ fontSize: '0.8em', display: 'block', color: '#666' }}>
                                      (Target: {JSON.parse(record.target_exercises || '[]').length} suggested)
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span style={{ fontWeight: 'bold', fontSize: '1.1em', color: '#1976d2' }}>
                                    {record.target_word || exercises.find(e => e.id === record.exercise_id)?.hindi_word || "Unknown"}
                                  </span>
                                </td>
                                <td style={{ fontStyle: 'italic', color: '#555' }}>
                                  "{record.recognized_text || '-'}"
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span>Pron: {record.pronunciation_score}%</span>
                                    {record.accuracy_score && <span style={{ fontSize: '0.8em', color: '#666' }}>Acc: {record.accuracy_score}%</span>}
                                  </div>
                                </td>
                                <td>
                                  <span className={`error-${record.error_type}`}>{record.error_type}</span>
                                  {record.error_subtype && (
                                    <div style={{ fontSize: '0.8em', color: '#d32f2f', fontStyle: 'italic' }}>
                                      {record.error_subtype}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <span className={`severity-${record.severity_level || 'Mild'}`}>
                                    {record.severity_level || '-'}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    onClick={() => setExpandedRecord(expandedRecord === record.id ? null : record.id)}
                                    className="action-button"
                                    style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                                  >
                                    {expandedRecord === record.id ? 'Hide Details' : 'Analyze'}
                                  </button>
                                </td>
                              </tr>
                              {expandedRecord === record.id && (
                                <tr className="detail-row">
                                  <td colSpan="8" style={{ backgroundColor: '#f9f9f9', padding: '15px' }}>
                                    <div className="clinical-detail" style={{ textAlign: 'left' }}>
                                      <h4 style={{ marginTop: 0, color: '#1976d2' }}>Session Details</h4>

                                      <div className="feedback-section">
                                        <h5>Therapist Notes</h5>
                                        <div style={{ position: 'relative' }}>
                                          <textarea
                                            className="notes-textarea"
                                            value={record.notes || notesMap[record.id] || ''}
                                            onChange={(e) => setNotesMap(prev => ({ ...prev, [record.id]: e.target.value }))}
                                            placeholder="Add your notes here..."
                                            style={{ width: '100%', minHeight: '100px' }}
                                          />
                                          <button
                                            className="notes-save-btn"
                                            onClick={() => updateProgressNotes(record.id, notesMap[record.id] || record.notes || '')}
                                            style={{ marginTop: '10px' }}
                                          >
                                            Save Notes
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Review Tab — verify & approve AI-generated assessments */}
      {mainTab === 'articulation' && subTab === 'review' && (
        <div className="section">
          <h2>Assessment Review</h2>
          <p style={{ color: '#666', marginTop: 0 }}>
            Listen to each recording, correct the AI results if needed, then approve or reject.
          </p>

          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '15px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Status</label>
              <select value={reviewStatusFilter} onChange={(e) => setReviewStatusFilter(e.target.value)}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Child (optional)</label>
              <select value={reviewChildFilter} onChange={(e) => setReviewChildFilter(e.target.value)}>
                <option value="">All children</option>
                {children.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <button className="btn btn-secondary" onClick={() => loadReviewQueue()}>Refresh</button>
          </div>

          {reviewLoading ? (
            <p>Loading…</p>
          ) : reviewQueue.length === 0 ? (
            <p>No {reviewStatusFilter} records.</p>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Child</th>
                    <th>Target Word</th>
                    <th>What Child Said (AI)</th>
                    <th>AI Error</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewQueue.map(record => {
                    const edits = reviewEdits[record.id] || {};
                    const audio = reviewAudioUrls[record.id];
                    return (
                      <React.Fragment key={record.id}>
                        <tr>
                          <td>{new Date(record.session_date).toLocaleDateString()}</td>
                          <td>
                            {record.child_name || `#${record.child_id}`}
                            {/* An assessment recording was made at a known mic
                                distance in a measured room, as part of a case
                                file. It should not be read as everyday practice. */}
                            {record.capture_session_id ? (
                              <span className="capture-tag" title="Recorded in a Case History session">
                                session {record.capture_session_id}
                              </span>
                            ) : null}
                          </td>
                          <td style={{ fontWeight: 'bold', color: '#1976d2' }}>{record.target_word}</td>
                          <td style={{ fontStyle: 'italic', color: '#555' }}>"{record.recognized_text || '-'}"</td>
                          <td><span className={`error-${record.error_type}`}>{record.error_type || '-'}</span></td>
                          <td><span className={`review-badge review-${record.review_status}`}>{record.review_status}</span></td>
                          <td>
                            <button
                              onClick={() => toggleReviewRow(record)}
                              className="btn btn-sm btn-primary"
                            >
                              {expandedReview === record.id ? 'Close' : 'Review'}
                            </button>
                          </td>
                        </tr>
                        {expandedReview === record.id && (
                          <tr className="detail-row">
                            <td colSpan="7" style={{ backgroundColor: '#f9f9f9', padding: '15px', textAlign: 'left' }}>
                              {/* Recordings are stored under child_<id> in blob storage, so the
                                  folder name is shown next to the child's name — it is what locates
                                  a recording outside the app. */}
                              <div style={{ display: 'flex', gap: '30px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label>Child Name</label>
                                  <span>{record.child_name || 'Unknown'}</span>
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label>Child ID</label>
                                  <span>{record.child_id}</span>
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                  <label>Recording Folder</label>
                                  <span>child_{record.child_id}</span>
                                </div>
                              </div>

                              {/* Audio playback */}
                              <div style={{ marginBottom: '15px' }}>
                                <h5 style={{ margin: '0 0 6px' }}>Child's Recording</h5>
                                {audio === 'loading' && <span>Loading audio…</span>}
                                {audio === 'unavailable' && <span style={{ color: '#999' }}>Audio unavailable</span>}
                                {audio && audio !== 'loading' && audio !== 'unavailable' && (
                                  <audio controls src={audio} style={{ width: '100%', maxWidth: '480px' }} />
                                )}
                              </div>

                              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div className="form-group">
                                  <label>Therapist Label</label>
                                  <select value={edits.therapist_label} onChange={(e) => handleReviewEditChange(record.id, 'therapist_label', e.target.value)}>
                                    <option value="">—</option>
                                    <option value="correct">Correct</option>
                                    <option value="incorrect">Incorrect</option>
                                    <option value="needs_review">Needs Review</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Error Type</label>
                                  <select value={edits.error_type} onChange={(e) => handleReviewEditChange(record.id, 'error_type', e.target.value)}>
                                    <option value="">—</option>
                                    <option value="substitution">Substitution</option>
                                    <option value="omission">Omission</option>
                                    <option value="addition">Addition</option>
                                    <option value="distortion">Distortion</option>
                                    <option value="no_error">No Error</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Severity</label>
                                  <select value={edits.severity_level} onChange={(e) => handleReviewEditChange(record.id, 'severity_level', e.target.value)}>
                                    <option value="">—</option>
                                    <option value="Mild">Mild</option>
                                    <option value="Moderate">Moderate</option>
                                    <option value="Severe">Severe</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Audio Quality</label>
                                  <select value={edits.audio_quality} onChange={(e) => handleReviewEditChange(record.id, 'audio_quality', e.target.value)}>
                                    <option value="">—</option>
                                    <option value="good">Good</option>
                                    <option value="moderate">Moderate</option>
                                    <option value="poor">Poor</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                      type="checkbox"
                                      checked={!!edits.has_background_noise}
                                      onChange={(e) => handleReviewEditChange(record.id, 'has_background_noise', e.target.checked)}
                                    />
                                    Background noise present
                                  </label>
                                </div>
                                <div className="form-group">
                                  <label>Recognized Word / Transcript</label>
                                  <input
                                    type="text"
                                    value={edits.recognized_text}
                                    onChange={(e) => handleReviewEditChange(record.id, 'recognized_text', e.target.value)}
                                    placeholder="Correct the transcript if AI is wrong"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Expected IPA</label>
                                  <input
                                    type="text"
                                    value={edits.expected_ipa}
                                    onChange={(e) => handleReviewEditChange(record.id, 'expected_ipa', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Recognized IPA</label>
                                  <input
                                    type="text"
                                    value={edits.recognized_ipa}
                                    onChange={(e) => handleReviewEditChange(record.id, 'recognized_ipa', e.target.value)}
                                  />
                                  <small style={{ color: '#888' }}>Auto-filled from the transcript — edit if needed.</small>
                                </div>
                                <div className="form-group full-width" style={{ gridColumn: '1 / -1' }}>
                                  <label>Therapist Notes</label>
                                  <textarea
                                    value={edits.notes}
                                    onChange={(e) => handleReviewEditChange(record.id, 'notes', e.target.value)}
                                    placeholder="Add notes…"
                                    style={{ width: '100%', minHeight: '80px' }}
                                  />
                                </div>
                              </div>

                              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                <button className="btn btn-secondary" onClick={() => handleSaveReview(record.id)}>Save Edits</button>
                                <button className="btn btn-primary" onClick={() => handleReviewDecision(record.id, 'approved')}>Approve</button>
                                <button className="btn btn-danger" onClick={() => handleReviewDecision(record.id, 'rejected')}>Reject</button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Auditory Exercises Tab */}
      {mainTab === 'auditory' && subTab === 'exercises' && (
        <div>
          {/* Create/List Auditory Exercises */}
          <div className="section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Auditory Discrimination Exercises</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowAuditoryFilters(!showAuditoryFilters)}
                  className="btn"
                  style={{
                    background: showAuditoryFilters ? '#7B1FA2' : 'transparent',
                    color: showAuditoryFilters ? '#fff' : '#AB47BC',
                    border: '2px solid #AB47BC',
                    position: 'relative'
                  }}
                >
                  Filters Exercise
                  {activeAuditoryFilterCount > 0 && (
                    <span style={{
                      position: 'absolute', top: '-8px', right: '-8px',
                      background: '#f44336', color: '#fff', borderRadius: '50%',
                      width: '20px', height: '20px', fontSize: '0.7rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 'bold'
                    }}>
                      {activeAuditoryFilterCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (showAuditoryForm) {
                      resetAuditoryForm();
                    } else {
                      setEditingAuditoryExercise(null);
                      setAuditoryForm({ ...defaultAuditoryForm, options: defaultAuditoryForm.options.map(o => ({ ...o })) });
                      setShowAuditoryForm(true);
                    }
                  }}
                  className="btn btn-primary"
                  style={{ background: '#AB47BC' }}
                >
                  {showAuditoryForm ? 'Cancel' : '+ Create Exercise'}
                </button>
              </div>
            </div>

            {/* Advanced Filter Panel */}
            {showAuditoryFilters && (
              <div className="auditory-filter-panel">
                <div className="auditory-filter-grid">
                  <div className="filter-group">
                    <label>Exercise Type</label>
                    <select
                      value={auditoryFilters.exercise_type}
                      onChange={(e) => handleAuditoryFilterChange('exercise_type', e.target.value)}
                    >
                      <option value="all">All Types</option>
                      <option value="image_discrimination">🖼️ Image Discrimination</option>
                      <option value="audio_discrimination">🔊 Audio Discrimination</option>
                      <option value="same_or_different">🔀 Same or Different</option>
                      <option value="sound_identification">🔍 Sound Identification</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Language</label>
                    <select
                      value={auditoryFilters.language}
                      onChange={(e) => handleAuditoryFilterChange('language', e.target.value)}
                    >
                      <option value="all">All Languages</option>
                      <option value="hi-IN">🇮🇳 Hindi</option>
                      <option value="en-US">🇺🇸 English</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Difficulty</label>
                    <select
                      value={auditoryFilters.difficulty_level}
                      onChange={(e) => handleAuditoryFilterChange('difficulty_level', e.target.value)}
                    >
                      <option value="all">All Levels</option>
                      <option value="1">⭐ Easy</option>
                      <option value="2">⭐⭐ Medium</option>
                      <option value="3">⭐⭐⭐ Hard</option>
                    </select>
                  </div>
                  <div className="filter-group search-group">
                    <label>Search</label>
                    <input
                      type="text"
                      value={auditoryFilters.search}
                      onChange={(e) => handleAuditoryFilterChange('search', e.target.value)}
                      placeholder="Search by target word..."
                    />
                  </div>
                </div>
                <div className="auditory-filter-actions">
                  <span className="auditory-filter-count">
                    Showing {filteredAuditoryExercises.length} of {auditoryExercises.length} exercises
                  </span>
                  {activeAuditoryFilterCount > 0 && (
                    <button onClick={clearAuditoryFilters} className="btn btn-sm" style={{ background: '#ef5350', color: '#fff' }}>
                      ✕ Clear Filters
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Create Form */}
            {showAuditoryForm && (
              <div style={{ background: '#f9f0ff', padding: '20px', borderRadius: '12px', marginTop: '15px', border: '1px solid #CE93D8' }}>
                <h3>{editingAuditoryExercise ? '✏️ Edit Auditory Exercise' : 'New Auditory Exercise'}</h3>
                <div className="form" style={{ flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <select
                      value={auditoryForm.exercise_type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        let newOptions = auditoryForm.options;
                        if (newType === 'same_or_different') {
                          newOptions = [
                            { option_text: '', image_url: '', is_correct: true, display_order: 1 },
                            { option_text: '', image_url: '', is_correct: false, display_order: 2 }
                          ];

                        } else if (auditoryForm.options.length < 4) {
                          newOptions = [
                            { option_text: '', image_url: '', is_correct: true, display_order: 1 },
                            { option_text: '', image_url: '', is_correct: false, display_order: 2 },
                            { option_text: '', image_url: '', is_correct: false, display_order: 3 },
                            { option_text: '', image_url: '', is_correct: false, display_order: 4 }
                          ];
                        }
                        setAuditoryForm({ ...auditoryForm, exercise_type: newType, options: newOptions });
                      }}
                    >
                      <option value="image_discrimination">🖼️ Image Discrimination</option>
                      <option value="audio_discrimination">🔊 Audio Discrimination</option>
                      <option value="same_or_different">🔀 Same or Different</option>

                    </select>
                    <input
                      type="text"
                      placeholder="Target Word (e.g., कान)"
                      value={auditoryForm.target_word}
                      onChange={(e) => setAuditoryForm({ ...auditoryForm, target_word: e.target.value })}
                    />
                    <select
                      value={auditoryForm.language}
                      onChange={(e) => setAuditoryForm({ ...auditoryForm, language: e.target.value })}
                    >
                      <option value="hi-IN">Hindi</option>
                      <option value="en-US">English</option>
                    </select>
                    <select
                      value={auditoryForm.difficulty_level}
                      onChange={(e) => setAuditoryForm({ ...auditoryForm, difficulty_level: parseInt(e.target.value) })}
                    >
                      <option value={1}>Easy</option>
                      <option value={2}>Medium</option>
                      <option value={3}>Hard</option>
                    </select>
                  </div>

                  {auditoryForm.exercise_type === 'audio_discrimination' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '0.9rem', color: '#555', fontWeight: 'bold' }}>Target Image (shown to child)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handleAuditoryImageUpload(e.target.files[0], (url) => {
                            setAuditoryForm({ ...auditoryForm, target_image_url: url });
                          });
                        }}
                      />
                      {auditoryForm.target_image_url && <span style={{ fontSize: '0.8rem', color: '#4CAF50' }}>✓ Image uploaded: {auditoryForm.target_image_url.split('/').pop()}</span>}
                    </div>
                  )}

                  <h4>
                    {auditoryForm.exercise_type === 'same_or_different'
                      ? 'Sounds (mark correct if they ARE the same)'
                      : 'Options (mark one as correct ✓)'}
                  </h4>
                  {auditoryForm.options.map((opt, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', background: opt.is_correct ? '#e8f5e9' : '#fff', padding: '8px', borderRadius: '8px', border: opt.is_correct ? '2px solid #4CAF50' : '1px solid #ddd' }}>
                      <input
                        type="radio"
                        name="correctOption"
                        checked={opt.is_correct}
                        onChange={() => {
                          const newOpts = auditoryForm.options.map((o, i) => ({ ...o, is_correct: i === idx }));
                          setAuditoryForm({ ...auditoryForm, options: newOpts });
                        }}
                      />
                      <input
                        type="text"
                        placeholder={
                          auditoryForm.exercise_type === 'same_or_different'
                            ? `Sound ${idx + 1} (e.g., "ba")`
                            : `Option ${idx + 1} word`
                        }
                        value={opt.option_text}
                        onChange={(e) => {
                          const newOpts = [...auditoryForm.options];
                          newOpts[idx] = { ...newOpts[idx], option_text: e.target.value };
                          setAuditoryForm({ ...auditoryForm, options: newOpts });
                        }}
                        style={{ flex: 1 }}
                      />
                      {(auditoryForm.exercise_type === 'image_discrimination') && (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              handleAuditoryImageUpload(e.target.files[0], (url) => {
                                const newOpts = [...auditoryForm.options];
                                newOpts[idx] = { ...newOpts[idx], image_url: url };
                                setAuditoryForm({ ...auditoryForm, options: newOpts });
                              });
                            }}
                            style={{ fontSize: '0.8rem' }}
                          />
                          {opt.image_url && <span style={{ fontSize: '0.7rem', color: '#4CAF50' }}>✓ {opt.image_url.split('/').pop()}</span>}
                        </div>
                      )}
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>
                        {auditoryForm.exercise_type === 'same_or_different'
                          ? (idx === 0 ? '🔊 Sound 1' : '🔊 Sound 2')
                          : `#${idx + 1}`}
                      </span>
                    </div>
                  ))}

                  <button
                    onClick={handleCreateOrUpdateAuditoryExercise}
                    style={{ background: '#AB47BC', marginTop: '10px' }}
                  >
                    {editingAuditoryExercise ? 'Save Changes' : 'Create Exercise'}
                  </button>
                </div>
              </div>
            )}

            {/* Exercise List */}
            <div style={{ marginTop: '20px' }}>
              <div className="exercises-grid">
                {filteredAuditoryExercises.map(ex => (
                  <div
                    key={ex.id}
                    className="exercise-card"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: '0.75rem', background:
                          ex.exercise_type === 'image_discrimination' ? '#E1F5FE' :
                            ex.exercise_type === 'audio_discrimination' ? '#F3E5F5' :
                              ex.exercise_type === 'same_or_different' ? '#FFF3E0' :
                                '#E8F5E9',
                        padding: '2px 8px', borderRadius: '4px'
                      }}>
                        {ex.exercise_type === 'image_discrimination' ? '🖼️ Image' :
                          ex.exercise_type === 'audio_discrimination' ? '🔊 Audio' :
                            ex.exercise_type === 'same_or_different' ? '🔀 Same/Diff' :
                              '🔍 Sound ID'}
                      </span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditAuditoryExercise(ex); }}
                          style={{ background: '#2196F3', color: '#fff', padding: '4px 8px', fontSize: '0.7rem', borderRadius: '4px', border: 'none', cursor: 'pointer' }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteAuditoryExercise(ex.id); }}
                          style={{ background: '#f44336', color: '#fff', padding: '4px 8px', fontSize: '0.7rem', borderRadius: '4px', border: 'none', cursor: 'pointer' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="exercise-info">
                      <h4>{ex.target_word}</h4>
                      <span className="language-badge">
                        {ex.language === 'hi-IN' ? '🇮🇳 Hindi' : '🇺🇸 English'}
                      </span>
                      <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                        {ex.options?.length || 0} options · Level {ex.difficulty_level}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredAuditoryExercises.length === 0 && auditoryExercises.length > 0 && (
                <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>No exercises match your filters. Try adjusting your criteria.</p>
              )}
              {auditoryExercises.length === 0 && (
                <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>No auditory exercises yet. Create one above!</p>
              )}
            </div>
          </div>
        </div>
      )}

      {mainTab === 'auditory' && subTab === 'assignment' && (
        <div>
          {/* Assign Auditory Exercises */}
          <div className="section">
            <h2>Assign Auditory Exercises</h2>
            <div className="assignment-controls">
              <select
                value={auditoryAssignChild}
                onChange={(e) => setAuditoryAssignChild(e.target.value)}
              >
                <option value="">Select a Child</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name} (Group {child.age_group}, {child.severity})
                  </option>
                ))}
              </select>
            </div>

            {/* Filter exercises */}
            <div className="exercise-filters assignment-filters" style={{ marginTop: '12px' }}>
              <div className="filter-group">
                <label>Type</label>
                <select value={auditoryFilters.exercise_type} onChange={(e) => handleAuditoryFilterChange('exercise_type', e.target.value)}>
                  <option value="all">All Types</option>
                  <option value="image_discrimination">🖼️ Image</option>
                  <option value="audio_discrimination">🔊 Audio</option>
                  <option value="same_or_different">🔀 Same/Diff</option>
                  <option value="sound_identification">🔍 Sound ID</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Language</label>
                <select value={auditoryFilters.language} onChange={(e) => handleAuditoryFilterChange('language', e.target.value)}>
                  <option value="all">All</option>
                  <option value="hi-IN">🇮🇳 Hindi</option>
                  <option value="en-US">🇺🇸 English</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Difficulty</label>
                <select value={auditoryFilters.difficulty_level} onChange={(e) => handleAuditoryFilterChange('difficulty_level', e.target.value)}>
                  <option value="all">All Levels</option>
                  <option value="1">⭐ Easy</option>
                  <option value="2">⭐⭐ Medium</option>
                  <option value="3">⭐⭐⭐ Hard</option>
                </select>
              </div>
              <div className="filter-group search-group">
                <label>Search</label>
                <input type="text" value={auditoryFilters.search} onChange={(e) => handleAuditoryFilterChange('search', e.target.value)} placeholder="Search by target word..." />
              </div>
            </div>

            {/* Pick exercises to assign */}
            <div className="exercises-grid" style={{ marginTop: '12px' }}>
              {filteredAuditoryExercises.map(ex => (
                <div
                  key={ex.id}
                  className={`exercise-card ${selectedAuditoryExercises.includes(ex.id) ? 'selected' : ''}`}
                  onClick={() => toggleAuditorySelection(ex.id)}
                  style={{ borderColor: selectedAuditoryExercises.includes(ex.id) ? '#AB47BC' : '#ddd' }}
                >
                  <div className="exercise-info">
                    <h4>{ex.target_word}</h4>
                    <span style={{
                      fontSize: '0.72rem', display: 'inline-block', marginBottom: '4px', padding: '2px 8px', borderRadius: '4px',
                      background:
                        ex.exercise_type === 'image_discrimination' ? '#E1F5FE' :
                          ex.exercise_type === 'audio_discrimination' ? '#F3E5F5' :
                            ex.exercise_type === 'same_or_different' ? '#FFF3E0' : '#E8F5E9'
                    }}>
                      {ex.exercise_type === 'image_discrimination' ? '🖼️ Image' :
                        ex.exercise_type === 'audio_discrimination' ? '🔊 Audio' :
                          ex.exercise_type === 'same_or_different' ? '🔀 Same/Diff' : '🔍 Sound ID'}
                    </span>
                    <br />
                    <span className="language-badge">
                      {ex.language === 'hi-IN' ? '🇮🇳 Hindi' : '🇺🇸 English'}
                    </span>
                    <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                      {ex.options?.length || 0} options · Level {ex.difficulty_level}
                    </div>
                  </div>
                </div>
              ))}
              {filteredAuditoryExercises.length === 0 && (
                <p style={{ color: '#888' }}>No matching auditory exercises. Adjust the filters, or create some in the Exercises sub-tab.</p>
              )}
            </div>

            <div className="selected-count" style={{ marginTop: '10px' }}>
              <strong>Selected: {selectedAuditoryExercises.length} exercises</strong>
              {selectedAuditoryExercises.length > 0 && (
                <button onClick={() => setSelectedAuditoryExercises([])} className="clear-button">Clear</button>
              )}
            </div>
            <button
              onClick={handleAssignAuditoryExercises}
              disabled={!auditoryAssignChild || selectedAuditoryExercises.length === 0}
              className="assign-button"
              style={{ background: selectedAuditoryExercises.length > 0 ? '#AB47BC' : '#ccc', marginTop: '10px' }}
            >
              {selectedAuditoryExercises.length > 0
                ? `Assign ${selectedAuditoryExercises.length} Auditory Exercises`
                : 'Select exercises above to assign'
              }
            </button>
          </div>
        </div>
      )}

      {/* Auditory › Manage Assignment */}
      {mainTab === 'auditory' && subTab === 'manage-assignment' && (
        <div>
          {/* Manage Auditory Assignments */}
          <div className="section">
            <h2>Manage Auditory Assignments</h2>
            <select
              value={manageAuditoryChild}
              onChange={(e) => handleManageAuditoryChildSelect(e.target.value)}
            >
              <option value="">Select a Child</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>

            {manageAuditoryAssignments.length > 0 && (
              <div className="assignments-list">
                <h3>Active Auditory Assignments for {children.find(c => c.id == manageAuditoryChild)?.name}</h3>
                {manageAuditoryAssignments.map(assignment => {
                  const exerciseIds = JSON.parse(assignment.exercise_ids);
                  return (
                    <div key={assignment.id} className="assignment-item">
                      <div className="assignment-info">
                        <strong>Assignment #{assignment.id}</strong>
                        <p>{exerciseIds.length} exercises assigned</p>
                        <small>Assigned on: {new Date(assignment.created_at).toLocaleDateString()}</small>
                        {assignment.assigned_by && <small style={{ display: 'block' }}>By: {assignment.assigned_by}</small>}
                      </div>
                      <button
                        onClick={() => handleDeleteAuditoryAssignment(assignment.id)}
                        className="delete-button"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {manageAuditoryChild && manageAuditoryAssignments.length === 0 && (
              <p style={{ marginTop: '20px', color: '#666' }}>No active auditory assignments for this child.</p>
            )}
          </div>
        </div>
      )}

      {mainTab === 'auditory' && subTab === 'progress' && (
        <div>
          {/* Auditory Progress */}
          <div className="section">
            <h2>Auditory Progress</h2>
            <select
              value={auditoryProgressChild}
              onChange={(e) => handleAuditoryProgressSelect(e.target.value)}
            >
              <option value="">Select a Child</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>

            {auditoryProgress && (
              <div className="progress-report" style={{ marginTop: '15px' }}>
                <h3>Auditory Progress for {children.find(c => c.id == auditoryProgressChild)?.name}</h3>
                <div className="progress-summary">
                  <div className="stat">
                    <h4>Total Attempts</h4>
                    <p>{auditoryProgress.summary.total_attempts}</p>
                  </div>
                  <div className="stat">
                    <h4>Correct</h4>
                    <p>{auditoryProgress.summary.correct_count}</p>
                  </div>
                  <div className="stat">
                    <h4>Accuracy</h4>
                    <p>{auditoryProgress.summary.accuracy_percentage}%</p>
                  </div>
                </div>

                {auditoryProgress.summary.exercise_type_breakdown && (
                  <div className="error-breakdown">
                    <h4>Breakdown by Type</h4>
                    {Object.entries(auditoryProgress.summary.exercise_type_breakdown).map(([type, data]) => (
                      <div key={type} className="error-item">
                        <span className="error-type">{type === 'image_discrimination' ? '🖼️ Image' : type === 'audio_discrimination' ? '🔊 Audio' : type === 'same_or_different' ? '🔀 Same/Diff' : '🔍 Sound ID'}:</span>
                        <span className="error-count">{data.correct}/{data.total} ({data.accuracy}%)</span>
                      </div>
                    ))}
                  </div>
                )}

                {auditoryProgress.progress_records.length > 0 && (
                  <div className="progress-details">
                    <h4>Recent Attempts</h4>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Word</th>
                            <th>Type</th>
                            <th>Result</th>
                            <th>Response Time</th>
                            <th>Attempt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditoryProgress.progress_records.map(r => (
                            <tr key={r.id}>
                              <td>{new Date(r.session_date).toLocaleDateString()}</td>
                              <td style={{ fontWeight: 'bold' }}>{r.target_word}</td>
                              <td>{r.exercise_type === 'image_discrimination' ? '🖼️' : r.exercise_type === 'audio_discrimination' ? '🔊' : r.exercise_type === 'same_or_different' ? '🔀' : '🔍'}</td>
                              <td style={{ color: r.is_correct ? '#4CAF50' : '#f44336', fontWeight: 'bold' }}>
                                {r.is_correct ? '✅ Correct' : '❌ Wrong'}
                              </td>
                              <td>{r.response_time_ms ? `${(r.response_time_ms / 1000).toFixed(1)}s` : '-'}</td>
                              <td>#{r.attempt_number}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Oral Motor / Mouth Gym Tab */}
      {mainTab === 'oral-motor' && subTab === 'assignment' && (
        <div>
          <div className="section">
            <h2>Mouth Gym — Oral Motor Exercises</h2>
            <p style={{ color: '#888', marginBottom: '16px' }}>These exercises use the child's camera to check if they can make correct mouth shapes. Select exercises below and assign them to a child.</p>

            {/* Assign Section */}
            <div className="assignment-controls" style={{ marginBottom: '20px' }}>
              <select
                value={oralAssignChild}
                onChange={(e) => setOralAssignChild(e.target.value)}
              >
                <option value="">Select a Child</option>
                {children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name} (Group {child.age_group})
                  </option>
                ))}
              </select>
              <button
                onClick={() => setSelectedOralExercises(oralMotorExercises.map(e => e.id))}
                className="random-button"
              >
                Select All
              </button>
              <button
                onClick={() => setSelectedOralExercises([])}
                className="clear-button"
              >
                Clear
              </button>
            </div>

            {/* Exercise Cards */}
            <div className="exercises-grid">
              {oralMotorExercises.map(ex => (
                <div
                  key={ex.id}
                  className={`exercise-card ${selectedOralExercises.includes(ex.id) ? 'selected' : ''}`}
                  onClick={() => toggleOralExerciseSelection(ex.id)}
                >
                  <div className="exercise-image">
                    <div className="image-placeholder" style={{ fontSize: '2.5rem' }}>
                      {ex.target_shape_type === 'wide' ? '😁' :
                        ex.target_shape_type === 'round' ? '😮' :
                          ex.target_shape_type === 'open' ? '😲' :
                            ex.target_shape_type === 'tongue_up' ? '😛' :
                              ex.target_shape_type === 'closed' ? '😶' :
                                ex.target_shape_type === 'puff' ? '🐡' : '👄'}
                    </div>
                  </div>
                  <div className="exercise-info">
                    <h4>{ex.shape_name}</h4>
                    <span className="language-badge" style={{ background: '#FF9800', color: '#fff' }}>
                      {ex.sound_text || 'No sound'}
                    </span>
                    <div className="exercise-meta">
                      <span className="category-tag">Shape: {ex.target_shape_type}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="selected-count" style={{ marginTop: '16px' }}>
              <strong>Selected: {selectedOralExercises.length} / {oralMotorExercises.length}</strong>
            </div>

            <button
              onClick={handleAssignOralExercises}
              disabled={!oralAssignChild || selectedOralExercises.length === 0}
              className="assign-button"
            >
              {selectedOralExercises.length > 0
                ? `Assign ${selectedOralExercises.length} Mouth Gym Exercises`
                : 'Select exercises to assign'}
            </button>
          </div>
        </div>
      )}

      {mainTab === 'oral-motor' && subTab === 'sessions' && (
        <div>
          {/* Session History */}
          <div className="section" style={{ marginTop: '24px' }}>
            <h2> Mouth Gym Sessions</h2>
            <select
              value={oralSessionsChild}
              onChange={(e) => handleOralSessionsSelect(e.target.value)}
            >
              <option value="">Select a Child</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>

            {oralSessions.length > 0 && (
              <table className="progress-table" style={{ marginTop: '16px' }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Shapes</th>
                    <th>Mouth Matched</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {oralSessions.map(session => (
                    <tr key={session.id}>
                      <td>{new Date(session.completed_at).toLocaleDateString()}</td>
                      <td>{session.completed_shapes}/{session.total_shapes}</td>
                      <td>{session.mouth_match_count}/{session.total_shapes}</td>
                      <td>
                        <span style={{
                          color: session.mouth_match_count >= session.total_shapes * 0.7 ? '#4caf50' : '#ff9800',
                          fontWeight: 'bold'
                        }}>
                          {session.total_shapes > 0 ? Math.round(session.mouth_match_count / session.total_shapes * 100) : 0}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {oralSessionsChild && oralSessions.length === 0 && (
              <p style={{ color: '#999', marginTop: '12px' }}>No Mouth Gym sessions yet for this child.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}